import { useCallback, useEffect, useRef, useState } from 'react';

import snackBarTypes from '../../../../../../components/Snackbar/constants';
import { showSnackbar } from '../../../../../app';

import {
  DOCUMENT_ACTION_SNACKBAR_KEYS,
  PRESIGN_DOWNLOAD_ROUTE,
  SET_APPROVAL_ROUTE,
  SET_VALIDITY_RANGE_ROUTE,
  SET_VALIDITY_ROUTE,
  VOID_DOCUMENT_ROUTE,
} from './constants';
import {
  fetchDocumentHistory,
  fetchOnboardingDocuments,
  postDocumentAction,
  refreshDocumentation,
} from './documentActions';

const showDocumentActionSnackbar = (dispatch, message, isSuccess) => {
  dispatch(
    showSnackbar({
      message,
      type: isSuccess ? snackBarTypes.SUCCESS : snackBarTypes.FAIL,
    })
  );
};

const useEmployeeDocumentationActions = ({
  selectedUserId,
  token,
  refreshToken,
  dispatch,
  canWriteReviewState,
}) => {
  const [activeActionKey, setActiveActionKey] = useState(null);
  const [documentActionErrorKey, setDocumentActionErrorKey] = useState(null);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadHistoryDocumentType, setUploadHistoryDocumentType] = useState(null);
  const [pendingFormStatusChange, setPendingFormStatusChange] = useState(null);
  const [pendingFormValidityChange, setPendingFormValidityChange] = useState(null);
  const [expirationDocument, setExpirationDocument] = useState(null);
  const [pendingDeleteDocument, setPendingDeleteDocument] = useState(null);

  const selectedUserIdRef = useRef(selectedUserId);
  selectedUserIdRef.current = selectedUserId;

  useEffect(() => {
    if (!selectedUserId) return;

    setIsUploadOpen(false);
    setDocumentActionErrorKey(null);
    setUploadHistoryDocumentType(null);
    setPendingFormStatusChange(null);
    setPendingFormValidityChange(null);
    setExpirationDocument(null);
    setPendingDeleteDocument(null);

    fetchOnboardingDocuments(dispatch, selectedUserId);
  }, [dispatch, selectedUserId]);

  const handleOpenUploadHistory = useCallback(
    (documentType) => {
      if (!selectedUserIdRef.current) return;

      // Dispatch alongside the state update (rather than in a separate effect keyed off
      // uploadHistoryDocumentType) so the modal's first paint already reflects the loading
      // state instead of briefly showing stale/empty history.
      setUploadHistoryDocumentType(documentType);
      setDocumentActionErrorKey(null);
      fetchDocumentHistory(dispatch, selectedUserIdRef.current);
    },
    [dispatch]
  );

  const handleCloseUploadHistory = useCallback(() => {
    setUploadHistoryDocumentType(null);
    setPendingFormStatusChange(null);
    setPendingFormValidityChange(null);
    setExpirationDocument(null);
    setPendingDeleteDocument(null);
  }, []);

  const handleUploadSuccess = useCallback(() => {
    fetchOnboardingDocuments(dispatch, selectedUserIdRef.current);
  }, [dispatch]);

  const handleViewDocument = async (documentId) => {
    if (!documentId) return;

    setActiveActionKey(`view:${documentId}`);
    setDocumentActionErrorKey(null);

    try {
      const response = await postDocumentAction({
        route: PRESIGN_DOWNLOAD_ROUTE,
        data: { documentId },
        dispatch,
        token,
        refreshToken,
      });
      const presignedUrl = response?.data?.presignedUrl;
      if (!presignedUrl) {
        setDocumentActionErrorKey('userDetail.employeeDocumentation.actionError');
        return;
      }

      window.open(presignedUrl, '_blank', 'noopener,noreferrer');
    } catch {
      setDocumentActionErrorKey('userDetail.employeeDocumentation.actionError');
    } finally {
      setActiveActionKey(null);
    }
  };

  const handleRequestFormStatus = useCallback(
    (documentId, status) => {
      if (!canWriteReviewState || !documentId || !status) return;

      setPendingFormStatusChange({ documentId, status });
      setPendingFormValidityChange(null);
      setExpirationDocument(null);
      setPendingDeleteDocument(null);
      setDocumentActionErrorKey(null);
    },
    [canWriteReviewState]
  );

  const handleRequestFormValidity = useCallback(
    (documentId, status) => {
      if (!canWriteReviewState || !documentId || !status) return;

      setPendingFormValidityChange({ documentId, status });
      setPendingFormStatusChange(null);
      setExpirationDocument(null);
      setPendingDeleteDocument(null);
      setDocumentActionErrorKey(null);
    },
    [canWriteReviewState]
  );

  const handleRequestSetExpiration = useCallback(
    (document) => {
      if (!canWriteReviewState || !document?._id) return;

      setExpirationDocument(document);
      setPendingFormStatusChange(null);
      setPendingFormValidityChange(null);
      setPendingDeleteDocument(null);
      setDocumentActionErrorKey(null);
    },
    [canWriteReviewState]
  );

  const handleCancelReviewChange = useCallback(() => {
    if (
      activeActionKey?.startsWith('setApproval:') ||
      activeActionKey?.startsWith('setValidity:')
    ) {
      return;
    }
    setPendingFormStatusChange(null);
    setPendingFormValidityChange(null);
  }, [activeActionKey]);

  const handleCancelExpiration = useCallback(() => {
    if (activeActionKey?.startsWith('setValidityRange:')) return;
    setExpirationDocument(null);
  }, [activeActionKey]);

  const handleRequestDelete = useCallback(
    (document) => {
      if (!canWriteReviewState || !document?._id) return;

      setPendingDeleteDocument(document);
      setPendingFormStatusChange(null);
      setPendingFormValidityChange(null);
      setExpirationDocument(null);
      setDocumentActionErrorKey(null);
    },
    [canWriteReviewState]
  );

  const handleCancelDelete = useCallback(() => {
    if (activeActionKey?.startsWith('void:')) return;
    setPendingDeleteDocument(null);
  }, [activeActionKey]);

  const handleConfirmDelete = async () => {
    const documentId = pendingDeleteDocument?._id;
    if (!canWriteReviewState || !documentId) return;

    setActiveActionKey(`void:${documentId}`);
    setDocumentActionErrorKey(null);

    try {
      await postDocumentAction({
        route: VOID_DOCUMENT_ROUTE,
        data: { documentId },
        dispatch,
        token,
        refreshToken,
      });
      setPendingDeleteDocument(null);
      refreshDocumentation(dispatch, selectedUserIdRef.current);
      showDocumentActionSnackbar(dispatch, DOCUMENT_ACTION_SNACKBAR_KEYS.delete.success, true);
    } catch {
      setPendingDeleteDocument(null);
      showDocumentActionSnackbar(dispatch, DOCUMENT_ACTION_SNACKBAR_KEYS.delete.error, false);
    } finally {
      setActiveActionKey(null);
    }
  };

  const handleConfirmFormStatusChange = async () => {
    const documentId = pendingFormStatusChange?.documentId;
    const status = pendingFormStatusChange?.status;
    if (!canWriteReviewState || !documentId || !status) return;

    setActiveActionKey(`setApproval:${documentId}`);
    setDocumentActionErrorKey(null);

    try {
      await postDocumentAction({
        route: SET_APPROVAL_ROUTE,
        data: { documentId, status },
        dispatch,
        token,
        refreshToken,
      });
      setPendingFormStatusChange(null);
      refreshDocumentation(dispatch, selectedUserIdRef.current);
      showDocumentActionSnackbar(dispatch, DOCUMENT_ACTION_SNACKBAR_KEYS.approval.success, true);
    } catch {
      setPendingFormStatusChange(null);
      showDocumentActionSnackbar(dispatch, DOCUMENT_ACTION_SNACKBAR_KEYS.approval.error, false);
    } finally {
      setActiveActionKey(null);
    }
  };

  const handleConfirmFormValidityChange = async () => {
    const documentId = pendingFormValidityChange?.documentId;
    const status = pendingFormValidityChange?.status;
    if (!canWriteReviewState || !documentId || !status) return;

    setActiveActionKey(`setValidity:${documentId}`);
    setDocumentActionErrorKey(null);

    try {
      await postDocumentAction({
        route: SET_VALIDITY_ROUTE,
        data: { documentId, status },
        dispatch,
        token,
        refreshToken,
      });
      setPendingFormValidityChange(null);
      refreshDocumentation(dispatch, selectedUserIdRef.current);
      showDocumentActionSnackbar(dispatch, DOCUMENT_ACTION_SNACKBAR_KEYS.validity.success, true);
    } catch {
      setPendingFormValidityChange(null);
      showDocumentActionSnackbar(dispatch, DOCUMENT_ACTION_SNACKBAR_KEYS.validity.error, false);
    } finally {
      setActiveActionKey(null);
    }
  };

  const handleSaveExpiration = async ({ validFrom, validNoticeFrom, validThrough }) => {
    const documentId = expirationDocument?._id;
    if (!canWriteReviewState || !documentId || !validFrom || !validNoticeFrom || !validThrough) {
      return;
    }

    setActiveActionKey(`setValidityRange:${documentId}`);
    setDocumentActionErrorKey(null);

    try {
      await postDocumentAction({
        route: SET_VALIDITY_RANGE_ROUTE,
        data: { documentId, validFrom, validNoticeFrom, validThrough },
        dispatch,
        token,
        refreshToken,
      });
      setExpirationDocument(null);
      refreshDocumentation(dispatch, selectedUserIdRef.current);
      showDocumentActionSnackbar(dispatch, DOCUMENT_ACTION_SNACKBAR_KEYS.expiration.success, true);
    } catch {
      setExpirationDocument(null);
      showDocumentActionSnackbar(dispatch, DOCUMENT_ACTION_SNACKBAR_KEYS.expiration.error, false);
    } finally {
      setActiveActionKey(null);
    }
  };

  return {
    activeActionKey,
    documentActionErrorKey,
    expirationDocument,
    handleCancelDelete,
    handleCancelExpiration,
    handleCancelReviewChange,
    handleCloseUploadHistory,
    handleConfirmDelete,
    handleConfirmFormStatusChange,
    handleConfirmFormValidityChange,
    handleOpenUploadHistory,
    handleRequestDelete,
    handleRequestFormStatus,
    handleRequestFormValidity,
    handleRequestSetExpiration,
    handleSaveExpiration,
    handleUploadSuccess,
    handleViewDocument,
    isUploadOpen,
    pendingDeleteDocument,
    pendingFormStatusChange,
    pendingFormValidityChange,
    setIsUploadOpen,
    uploadHistoryDocumentType,
  };
};

export default useEmployeeDocumentationActions;
